import { useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  riskMapTabSchema,
  type RiskMapTab,
} from '@kirin/api-client/contracts/ai-risk-map';

export const RISK_MAP_TAB_PARAM = 'tab';
export const DEFAULT_RISK_MAP_TAB: RiskMapTab = 'flow';

/** Parse the `?tab=` value; anything unknown or missing falls back to Risk Flow. */
export function parseRiskMapTab(raw: string | null): RiskMapTab {
  const parsed = riskMapTabSchema.safeParse(raw);
  return parsed.success ? parsed.data : DEFAULT_RISK_MAP_TAB;
}

/**
 * The selected AI Risk Map tab, kept in the URL (`?tab=flow|blast|radar`) so a
 * reload or a shared link reopens the same tab. Other params are preserved.
 */
export function useRiskMapTab(): [RiskMapTab, (tab: RiskMapTab) => void] {
  const [searchParams, setSearchParams] = useSearchParams();
  const tab = parseRiskMapTab(searchParams.get(RISK_MAP_TAB_PARAM));

  const setTab = useCallback(
    (next: RiskMapTab) => {
      setSearchParams(
        prev => {
          const params = new URLSearchParams(prev);
          params.set(RISK_MAP_TAB_PARAM, next);
          return params;
        },
        { replace: true }
      );
    },
    [setSearchParams]
  );

  return [tab, setTab];
}
